import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useAskRedoEnabled } from "@redotech/redo-merchant-app-common/ask-redo/use-ask-redo-enabled";
import { ClientUploader } from "@redotech/redo-merchant-app-common/client/upload";
import { useEscalationContext } from "@redotech/redo-merchant-app-common/escalation";
import { GlobalAgentContext } from "@redotech/redo-merchant-app-common/global-agent-context";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { ReloadOutboundViewsContext } from "@redotech/redo-merchant-fulfillment-app/outbound-views";
import { ReloadParcelsContext } from "@redotech/redo-merchant-fulfillment-app/services/parcel-service";
import { Message } from "@redotech/redo-model/agents/llm-messages";
import {
  AiChatProvider,
  AiChatStreamEvent,
} from "@redotech/redo-web/ai/chat/contexts/ai-chat-context";
import { useAiSlidePanel } from "@redotech/redo-web/ai/chat/contexts/ai-slide-panel-context";
import {
  extractAttachmentUrls,
  extractTextFromContent,
  transformMessagesForBackend,
} from "@redotech/redo-web/ai/chat/utils/messages";
import {
  ReloadTeamContext,
  TeamContext,
} from "@redotech/redo-web/context/team-context";
import { UploaderContext } from "@redotech/redo-web/image-upload";
import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getCustomAnalyticsReportFilterOptionsQueryKey } from "../../../analytics/reports/custom/hooks/use-custom-analytics-report-filter-options-query";
import { getCustomAnalyticsReportQueryInvalidationKey } from "../../../analytics/reports/custom/hooks/use-custom-analytics-report-query";
import { getCustomAnalyticsReportsQueryInvalidationKey } from "../../../analytics/reports/custom/hooks/use-custom-analytics-reports-query";
import { EscalateToHumanTool } from "../../help-chat/escalate-to-human-tool";
import { HelpChatGlobal } from "../../help-chat/help-chat-global";
import { CreateHyperLinkTool } from "./global-search-tools/create-hyper-link-tool";
import {
  CUSTOM_ANALYTICS_REPORT_LINK_PREFIX,
  CustomAnalyticsReportMarkdownLink,
  linkifyCustomAnalyticsReportUrls,
} from "./global-search-tools/custom-analytics-report-markdown-link";
import { UpdateIntegrationTool } from "./global-search-tools/update-integration-tool";

interface GlobalAIProps {
  children?: React.ReactNode;
}

function isOmsSettingsPage(): boolean {
  const omsSettingsPaths: string[] = [
    "/settings/oms",
    "/outbound-labels/packing-slips",
  ];
  return omsSettingsPaths.some((path) =>
    window.location.pathname.includes(path),
  );
}

function messagesContainCustomAnalyticsReportsLink(messages: Message[]) {
  return messages.some((message) => {
    if (message.role === "tool" || message.role === "system") {
      return false;
    }

    const content = message.content;
    const text =
      typeof content === "string"
        ? content
        : Array.isArray(content)
          ? content
              .filter((part) => part.type === "text")
              .map((part) => part.text)
              .join("\n")
          : "";

    return text.includes(CUSTOM_ANALYTICS_REPORT_LINK_PREFIX);
  });
}

function useInvalidateCustomAnalyticsReports() {
  const queryClient = useQueryClient();
  const team = useRequiredContext(TeamContext);

  return useHandler(() => {
    void queryClient.invalidateQueries({
      queryKey: getCustomAnalyticsReportsQueryInvalidationKey(team._id),
      exact: false,
    });
    void queryClient.invalidateQueries({
      queryKey: getCustomAnalyticsReportFilterOptionsQueryKey(team._id),
    });
    void queryClient.invalidateQueries({
      queryKey: getCustomAnalyticsReportQueryInvalidationKey(team._id),
      exact: false,
    });
  });
}

function useGlobalAiToolResultHandler(
  toolResultHandlers?: Record<string, ((result: unknown) => void) | undefined>,
) {
  return useHandler(async (args: { toolName: string; result: unknown }) => {
    const pageToolResultHandler = toolResultHandlers?.[args.toolName];
    if (pageToolResultHandler) {
      pageToolResultHandler(args.result);
      return;
    }

    if (args.toolName === "escalate-to-human") {
      // No command palette to close in AI-only mode
      return;
    }
  });
}

export const GlobalAI = memo(function GlobalAI({ children }: GlobalAIProps) {
  // EscalationProvider used to live here, but
  // `<GlobalAI />` is rendered as the `panel` prop of
  // `AiSlidePanelLayout` — i.e., a sibling of the app's children,
  // not a parent. That meant the Ask Redo layout (which mounts
  // `AskRedoEscalationWatcher` above the slide-panel) had no
  // EscalationProvider above it and every page crashed.
  // Providers are now mounted at `page.tsx` so they wrap both
  // GlobalAI and AskRedoLayout. GlobalAI just consumes them.
  //
  // When Ask Redo is enabled, the merchant uses the Ask Redo slide-
  // out — help-chat is dormant. We bail here so help-chat's
  // `useEscalationSubscription` doesn't open a parallel Notice stream
  // and fire its own toast every time the Ask Redo slide-out (open
  // OR closed) receives a human reply. One chat owns the
  // escalation pipeline at a time.
  //
  // Also bail while the flag is still resolving (`pending`). Without
  // this, an Ask-Redo-enabled team briefly mounts help-chat during
  // the LD flag load: `GlobalAIContentWithEscalation` kicks off
  // `useEscalationNotifications` recovery and can show a toast for
  // an escalation that's about to be picked up by the Ask Redo
  // watcher instead, then unmount when the flag resolves. Better to
  // render nothing until we know which path the user is on.
  const { enabled: askRedoEnabled, pending: askRedoPending } =
    useAskRedoEnabled();
  if (askRedoPending || askRedoEnabled) {
    return null;
  }
  return (
    <ClientUploader>
      <GlobalAIContentWithEscalation>{children}</GlobalAIContentWithEscalation>
    </ClientUploader>
  );
});

const GlobalAIContentWithEscalation = memo(
  function GlobalAIContentWithEscalation({ children }: GlobalAIProps) {
    const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
    const user = useRequiredContext(UserContext);
    const uploader = useContext(UploaderContext);
    const {
      pageContext,
      onPageDataChanged,
      toolComponents: pageToolComponents,
      toolResultHandlers: pageToolResultHandlers,
    } = useContext(GlobalAgentContext);
    const {
      escalation,
      setEscalation,
      setTriageThreadId: setEscalationTriageThreadId,
      clearEscalation,
    } = useEscalationContext();
    const { panelOptions, clearPanelOptions } = useAiSlidePanel();
    const reloadViews = useContext(ReloadOutboundViewsContext);
    const reloadParcels = useContext(ReloadParcelsContext);
    const reloadTeam = useContext(ReloadTeamContext);

    // Handle direct escalation from external flows (e.g., recurring pickup requests)
    useEffect(() => {
      if (!panelOptions?.directEscalation) {
        return;
      }
      const {
        escalationId,
        intercomConversationId,
        noticeTopic,
        triageThreadId,
      } = panelOptions.directEscalation;
      setEscalationTriageThreadId(triageThreadId);
      setEscalation({
        escalationId,
        intercomConversationId,
        noticeTopic,
        status: "waiting",
        triageThreadId,
      });
      clearPanelOptions();
    }, [
      panelOptions,
      setEscalation,
      setEscalationTriageThreadId,
      clearPanelOptions,
    ]);

    // state is used to control the UI thread context while refs are used during sends
    const [_currentThreadId, setCurrentThreadId] = useState<
      string | undefined
    >();
    const [_triageThreadId, setTriageThreadId] = useState<string | undefined>();
    const triageThreadCallbackRef = useRef<((threadId: string) => void) | null>(
      null,
    );

    // Use refs to always have the most current values for the callback
    const currentThreadIdRef = useRef<string | undefined>();
    const triageThreadIdRef = useRef<string | undefined>();

    const toolComponents = useMemo(
      () => ({
        "update-integration": UpdateIntegrationTool,
        "create-hyper-link": CreateHyperLinkTool,
        "escalate-to-human": EscalateToHumanTool,
        ...pageToolComponents,
      }),
      [pageToolComponents],
    );

    const handleToolResult = useGlobalAiToolResultHandler(
      pageToolResultHandlers,
    );
    const invalidateCustomAnalyticsReports =
      useInvalidateCustomAnalyticsReports();

    const handleChatOpen = useCallback(() => {
      setCurrentThreadId(undefined);
      setTriageThreadId(undefined);
      currentThreadIdRef.current = undefined;
      triageThreadIdRef.current = undefined;
      // Also clear escalation triageThreadId when starting new chat
      setEscalationTriageThreadId(null);
    }, [setEscalationTriageThreadId]);

    const handleLoadThread = useCallback(
      (threadId: string, _thread: unknown) => {
        // When loading from thread history, always continue with the triage agent
        // The triage agent will handle routing to child agents as needed
        // Continue with triage agent
        setCurrentThreadId(threadId);
        setTriageThreadId(threadId);
        currentThreadIdRef.current = threadId;
        triageThreadIdRef.current = threadId;
        // Update escalation context with the triage thread ID
        setEscalationTriageThreadId(threadId);
      },
      [setEscalationTriageThreadId],
    );

    const streamMessageToAI = useCallback(
      async function* ({
        newMessages,
        signal,
      }: {
        newMessages: Message[];
        signal: AbortSignal;
      }): AsyncGenerator<AiChatStreamEvent, void, undefined> {
        // If escalation is active with human support, route messages to Intercom instead of AI
        if (
          escalation.status === "human-active" &&
          escalation.escalationId &&
          newMessages.length > 0
        ) {
          const lastMessage = newMessages[newMessages.length - 1];
          if (lastMessage && lastMessage.role === "user") {
            // Extract text content (filtering out hidden image-data and video-data metadata)
            const messageContent = extractTextFromContent(lastMessage.content);

            // Extract image and video URLs from the message content
            const attachmentUrls = Array.isArray(lastMessage.content)
              ? extractAttachmentUrls(lastMessage.content)
              : [];

            // Send message if there's text or attachments
            if (messageContent || attachmentUrls.length > 0) {
              const result = await merchantRpcClient.sendEscalationMessage({
                escalationId: escalation.escalationId,
                message: messageContent || "",
                ...(attachmentUrls.length > 0 && { attachmentUrls }),
              });

              if (!result.success) {
                yield { type: "error", error: "Failed to send message" };
                return;
              }

              // Return empty messages since the AI doesn't respond during human support
              yield { type: "complete", messages: [] };
              return;
            }
          }
        }

        // Clear closed escalation context so the user can re-escalate
        if (escalation.status === "closed") {
          clearEscalation();
        }

        const transformedMessages = transformMessagesForBackend(newMessages);

        // Use refs to get the most current values
        const currentCurrentThreadId = currentThreadIdRef.current;
        const currentTriageThreadId = triageThreadIdRef.current;

        // Page context: if set (e.g., from Returns Ask AI), always include it
        // so the triage agent has consistent context on this page.

        const stream = merchantRpcClient.streamMerchantSupportAgent(
          {
            messages: transformedMessages,
            currentThreadId: currentCurrentThreadId,
            triageThreadId: currentTriageThreadId,
            userEmail: user.email,
            merchantTimezone: Temporal.Now.timeZoneId(),
            // Always pass pageContext when available (not only for new threads)
            ...(pageContext ? { pageContext } : {}),
          },
          { signal },
        );

        let sawOmsToolCall = false;
        for await (const event of stream) {
          switch (event.type) {
            case "text-delta":
              yield { type: "text-delta", textDelta: event.textDelta };
              break;
            case "tool-call":
              if (event.toolCall.toolName === "query-oms") {
                sawOmsToolCall = true;
              }
              yield { type: "tool-call", toolCall: event.toolCall };
              break;
            case "reasoning":
              yield { type: "reasoning", text: event.text };
              break;
            case "status":
              yield { type: "status", status: event.status };
              break;
            case "complete": {
              const { thread } = event;

              // Check if this created a new triage thread
              const wasNewTriageThread =
                !currentTriageThreadId && thread.triageThreadId;

              // Update both state and refs
              setCurrentThreadId(thread.currentThreadId.toString());
              setTriageThreadId(thread.triageThreadId.toString());
              currentThreadIdRef.current = thread.currentThreadId.toString();
              triageThreadIdRef.current = thread.triageThreadId.toString();

              // If a new triage thread was created, trigger thread refresh and update escalation context
              if (wasNewTriageThread) {
                triageThreadCallbackRef.current?.(
                  thread.triageThreadId.toString(),
                );
                setEscalationTriageThreadId(thread.triageThreadId.toString());
              }

              // Refresh sidebar views after OMS queries (may have created/modified views)
              if (sawOmsToolCall) {
                void reloadViews?.({ onlyReloadIfChanged: true });
                void reloadParcels?.();
                onPageDataChanged?.();
                if (isOmsSettingsPage()) {
                  void reloadTeam?.();
                }
              }

              if (
                messagesContainCustomAnalyticsReportsLink(thread.newMessages)
              ) {
                invalidateCustomAnalyticsReports();
              }

              yield { type: "complete", messages: thread.newMessages };
              break;
            }
            case "error":
              yield { type: "error", error: event.error };
              break;
          }
        }
      },
      [
        merchantRpcClient,
        user.email,
        pageContext,
        escalation.status,
        escalation.escalationId,
        clearEscalation,
        setEscalationTriageThreadId,
        reloadViews,
        reloadParcels,
        reloadTeam,
        onPageDataChanged,
        invalidateCustomAnalyticsReports,
      ],
    );

    useEffect(() => {
      return () => {
        triageThreadCallbackRef.current = null;
      };
    }, []);

    // Enable file uploads for both AI chat and human support
    const chatUploader = uploader;

    const isHumanSupport = escalation.status === "human-active";

    const fileOptions = useMemo(
      () => ({
        inputAccept: isHumanSupport ? "image/*,video/*" : "image/*",
        inputMultiple: true,
        inputLabel: "Upload from computer",
      }),
      [isHumanSupport],
    );

    return (
      <AiChatProvider
        additionalToolComponents={toolComponents}
        fileUploadOptions={fileOptions}
        isHumanSupportMode={isHumanSupport}
        markdownLinkComponent={CustomAnalyticsReportMarkdownLink}
        markdownTextTransform={linkifyCustomAnalyticsReportUrls}
        onToolResult={handleToolResult}
        streamMessageToAI={streamMessageToAI}
        uploader={chatUploader}
      >
        {children}
        <HelpChatGlobal
          onChatOpen={handleChatOpen}
          onLoadThread={(threadId: string, thread: unknown) =>
            handleLoadThread(threadId, thread)
          }
          onRegisterTriageThreadCallback={(
            callback: (threadId: string) => void,
          ) => {
            triageThreadCallbackRef.current = callback;
          }}
        />
      </AiChatProvider>
    );
  },
);
